// Gradient Builder - CSS Preview Generator
// Converts GradientConfig to CSS gradient string for visual preview

import type {
  GradientConfig,
  LinearGradientConfig,
  RadialGradientConfig,
  SweepGradientConfig,
  ColorStop,
} from "../model/gradient";

/**
 * Format color stops for CSS
 */
function formatCssColorStops(colors: ColorStop[]): string {
  return colors
    .map(stop => `${stop.color} ${(stop.offset * 100).toFixed(1)}%`)
    .join(', ');
}

/**
 * Convert Linear Gradient to CSS
 * Flutter angle 0° = right, CSS angle 90° = right
 * Flutter uses begin/end alignment, CSS uses angle
 */
function linearGradientToCss(config: LinearGradientConfig): string {
  // Convert Flutter angle to CSS angle
  // Flutter: 0° = left-to-right, 90° = top-to-bottom
  // CSS: 0° = bottom-to-top, 90° = left-to-right
  // Conversion: CSS angle = Flutter angle + 90°
  const cssAngle = config.angle + 90;

  return `linear-gradient(${cssAngle}deg, ${formatCssColorStops(config.colors)})`;
}

/**
 * Convert Radial Gradient to CSS
 * Flutter uses center alignment + radius
 * CSS uses position keywords + size
 */
function radialGradientToCss(config: RadialGradientConfig): string {
  // Map Flutter center to CSS position
  const positionMap: Record<string, string> = {
    center: 'center center',
    topCenter: 'center top',
    bottomCenter: 'center bottom',
    centerLeft: 'left center',
    centerRight: 'right center',
  };

  const position = positionMap[config.center] || 'center center';

  // Scale color stops by inverse of radius to simulate radius effect
  // radius 1.0 = normal, radius 0.5 = stops at 2x positions (smaller gradient)
  const scaleFactor = 1 / config.radius;
  const scaledColorStops = config.colors
    .map(stop => `${stop.color} ${(stop.offset * 100 * scaleFactor).toFixed(1)}%`)
    .join(', ');

  return `radial-gradient(circle at ${position}, ${scaledColorStops})`;
}

/**
 * Convert Sweep Gradient to CSS (conic-gradient)
 * Flutter uses radians, CSS uses degrees
 * Flutter: startAngle/endAngle in radians
 * CSS: from angle in degrees
 */
function sweepGradientToCss(config: SweepGradientConfig): string {
  // Map Flutter center to CSS position
  const positionMap: Record<string, string> = {
    center: 'center center',
    topCenter: 'center top',
    bottomCenter: 'center bottom',
    centerLeft: 'left center',
    centerRight: 'right center',
  };

  const position = positionMap[config.center] || 'center center';

  // Convert radians to degrees
  const startDeg = (config.startAngle * 180) / Math.PI;
  const endDeg = (config.endAngle * 180) / Math.PI;

  // Calculate the color stops with proper angle distribution
  const angleRange = endDeg - startDeg;
  const colorStopsWithAngles = config.colors.map(stop => {
    const angle = startDeg + (stop.offset * angleRange);
    return `${stop.color} ${angle.toFixed(1)}deg`;
  }).join(', ');

  return `conic-gradient(from ${startDeg.toFixed(1)}deg at ${position}, ${colorStopsWithAngles})`;
}

/**
 * Main function: Convert GradientConfig to CSS gradient string
 */
export function gradientToCss(config: GradientConfig): string {
  switch (config.type) {
    case 'linear':
      return linearGradientToCss(config);
    case 'radial':
      return radialGradientToCss(config);
    case 'sweep':
      return sweepGradientToCss(config);
    default:
      throw new Error(`Unknown gradient type: ${(config as any).type}`);
  }
}
